import type {ReactElement} from 'react';
import React from 'react';
import {BaseTintAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/base-tint-adjustment-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromEffectsOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveSlideItem';
import {useGetKeyFromEffectsActiveItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveItem';
import type {RGBA} from '@Utils/color.util';

interface TintAdjustmentOptionProps {
  label?: string;
  panelId: string;

  onBack(): void;

  onClickColorFill(): void;
}

export function TintAdjustmentOption({...props}: TintAdjustmentOptionProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const slideItemTint = useGetKeyFromEffectsOfActiveSlideItem('tint');
  const slideItemTintColor = useGetKeyFromEffectsOfActiveSlideItem('tintColor');
  const activeItemTint = useGetKeyFromEffectsActiveItem('tint');
  const activeItemTintColor = useGetKeyFromEffectsActiveItem('tintColor');

  if (!activeItem) {
    return null;
  }

  if (activeItem.isSlideshow() && activeSlideUID === undefined) {
    return null;
  }

  const toggleTintForSlideItem = (): void => {
    if (activeItem.isSlideshow() && activeSlide?.isMediaSlide()) {
      void activeItem.updateSlideFromObject(activeSlide.uid, {
        effects: {
          tint: !slideItemTint,
        },
      });
    }
  };

  const onToggleTint = (): void => {
    if (!('effects' in activeItem)) {
      return;
    }
    void activeItem.updateFromObject({
      effects: {
        tint: !activeItemTint,
      },
    });
  };

  const updateOpacityForSlideItem = (tintOpacity: number, undoable = true): void => {
    if (activeItem.isSlideshow() && activeSlide?.isMediaSlide() && slideItemTintColor !== undefined) {
      const tintColor = slideItemTintColor.slice() as RGBA;
      tintColor[3] = tintOpacity;
      void activeItem.updateSlideFromObject(
        activeSlide.uid,
        {
          effects: {
            tintColor,
          },
        },
        {
          undoable,
        }
      );
    }
  };

  const updateOpacity = (tintOpacity: number, undoable = true): void => {
    if (activeItemTintColor !== undefined) {
      const tintColor = activeItemTintColor.slice() as RGBA;
      tintColor[3] = tintOpacity;
      void activeItem.updateFromObject(
        {
          effects: {
            tintColor,
          },
        },
        {
          undoable,
        }
      );
    }
  };

  const getDefaultContent = (): ReactElement | null => {
    if (activeItem.isSlideshow() && activeSlide?.isMediaSlide() && !(slideItemTint === undefined || !slideItemTintColor)) {
      return (
        <BaseTintAdjustmentOption
          label={props.label}
          panelId={props.panelId}
          tint={slideItemTint}
          tintColor={slideItemTintColor}
          tintOpacity={slideItemTintColor[3]}
          onToggleTint={toggleTintForSlideItem}
          onClickColorFill={(): void => {
            props.onClickColorFill();
          }}
          updateOpacity={updateOpacityForSlideItem}
        />
      );
    }

    if ((activeItem.isImage() || activeItem.isVideo() || activeItem.isSticker()) && activeItemTint !== undefined && activeItemTintColor !== undefined) {
      return (
        <BaseTintAdjustmentOption
          label={props.label}
          panelId={props.panelId}
          tint={activeItemTint}
          tintColor={activeItemTintColor}
          tintOpacity={activeItemTintColor[3]}
          onToggleTint={onToggleTint}
          onClickColorFill={(): void => {
            props.onClickColorFill();
          }}
          updateOpacity={updateOpacity}
        />
      );
    }

    return null;
  };

  return getDefaultContent();
}
